/// <amd-module name="Build/Admin/PoolsList/Components/CreateNewPoolContainer" />
import * as React from "react";

import { TaskAgentCloud, TaskAgentPool, TaskAgentQueue } from "DistributedTask/Client/Contracts/TaskAgent";

import { IProjectData } from "Tfs/Platform/Page";

import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";

import { IButtonProps } from "VSSUI/Button";
import { Dropdown } from "VSSUI/Dropdown";
import { IListBoxItem } from "VSSUI/ListBox";

import { IProjectDataService } from "Build/Admin/Common/Services/ProjectDataService";
import { PoolsListActionCreator } from "Build/Admin/PoolsList/Actions/PoolsListActionCreator";
import { PipelinePermissionsSettings } from "Build/Admin/PoolsList/Components/PipelinePermissionsSettings";
import { PoolNameDescriptionSettings } from "Build/Admin/PoolsList/Components/PoolNameDescriptionSettings";
import { StoresHub } from "Build/Admin/PoolsList/Stores/StoresHub";

import * as Resources from "Build/Admin/PoolsList/Resources";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";

export interface ICreateNewPoolContainerProps {
    actionCreator: PoolsListActionCreator;
    storesHub: StoresHub;
    showAgentCloudDropdown: boolean;
    onFooterButtonPropsChanged(footerButtonProps: IButtonProps[]): void;
}

interface ICreateNewPoolContainerState {
    poolName?: string;
    poolDescription?: string;
    authorizeAllPipelines: boolean;
    autoProvisionProjectPools: boolean;
    agentCloudOptions: IListBoxItem<TaskAgentCloud>[];
    selectedAgentCloud?: TaskAgentCloud;
    disableSubmitButton: boolean;
}

export class CreateNewPoolContainer extends VssComponent<ICreateNewPoolContainerProps, ICreateNewPoolContainerState> {
    private _project: IProjectData | undefined;
    private _disableAllPipelinesSetting: boolean;
    private _showGrantAccessPermissionToAllPipelines: boolean;

    constructor(props: ICreateNewPoolContainerProps, context: IVssComponentContext) {
        super(props, context);

        const projectDataService: IProjectDataService = context.pageContext.getService<IProjectDataService>("IProjectDataService");
        this._project = projectDataService.getProjectData();

        this._disableAllPipelinesSetting = !this.props.storesHub.securityStore.state.hasManageAllPermission;
        this._showGrantAccessPermissionToAllPipelines = isFeatureFlagEnabled(this.context.pageContext, "DistributedTask.EnableGrantOrgLevelAccessPermissionToAllPipelinesInAgentPools", false);

        this.state = this._getStateFromStores();
    }

    public componentDidMount(): void {
        this.props.onFooterButtonPropsChanged(this._getFooterButtonProps());
    }

    public componentDidUpdate(prevProps: ICreateNewPoolContainerProps, prevState: ICreateNewPoolContainerState): void {
        if (prevState.disableSubmitButton !== this.state.disableSubmitButton) {
            this.props.onFooterButtonPropsChanged(this._getFooterButtonProps());
        }
    }

    public render(): JSX.Element {
        var poolIsCreatedInProjectLevel = this._project !== undefined;

        return (
            <div className="create-new-pool-container">
                <ConditionalChildren renderChildren={this.props.showAgentCloudDropdown}>
                    <div className="pool-setting-label">
                        {Resources.AgentCloudLabel}
                    </div>
                    <Dropdown
                        className="agent-cloud-dropdown"
                        disabled={this.state.agentCloudOptions.length === 0}
                        placeholder={Resources.SelectAgentCloudLabel}
                        items={this.state.agentCloudOptions}
                        onSelect={this._onAgentCloudSelectionChanged} />
                </ConditionalChildren>
                <PoolNameDescriptionSettings
                    poolName={this.state.poolName}
                    poolDescription={this.state.poolDescription}
                    onSettingsChanged={this._onPoolNameDescriptionChanged} />
                <PipelinePermissionsSettings
                    disableAllPipelinesSetting={this._disableAllPipelinesSetting}
                    authorizeAllPipelines={this.state.authorizeAllPipelines}
                    autoProvisionProjectPools={this.state.autoProvisionProjectPools}
                    showAuthorizeAllPipelinesOption={poolIsCreatedInProjectLevel || this._showGrantAccessPermissionToAllPipelines}
                    showAutoProvisionOption={!poolIsCreatedInProjectLevel}
                    onSettingsChanged={this._onPermissionsChanged} />
            </div>
        );
    }

    private _onAgentCloudSelectionChanged = (event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<TaskAgentCloud>): void => {
        this.setState({
            selectedAgentCloud: item.data,
            disableSubmitButton: this._shouldDisableSubmitButton(this.state.poolName, item.data)
        });
    }

    private _onPoolNameDescriptionChanged = (poolName: string | undefined, poolDescription: string | undefined): void => {
        this.setState({
            poolName,
            poolDescription,
            disableSubmitButton: this._shouldDisableSubmitButton(poolName, this.state.selectedAgentCloud)
        });
    }

    private _onPermissionsChanged = (authorizeAllPipelines: boolean, autoProvisionProjectPools: boolean): void => {
        this.setState({
            authorizeAllPipelines,
            autoProvisionProjectPools,
            disableSubmitButton: this._shouldDisableSubmitButton(this.state.poolName, this.state.selectedAgentCloud)
        });
    }

    private _getStateFromStores = (): ICreateNewPoolContainerState => {
        // This is used on panel initialization, and to reset the panel state on open/close
        // so some of the panel's fields are set to their default values below.
        // The panel's fields are set by setState calls in the child component callbacks in the render function.
        return {
            poolName: undefined,
            poolDescription: undefined,
            authorizeAllPipelines: false,
            autoProvisionProjectPools: this._project === undefined,
            agentCloudOptions: this._getAgentCloudOptions(),
            selectedAgentCloud: undefined,
            disableSubmitButton: true
        };
    }

    private _getAgentCloudOptions = (): IListBoxItem<TaskAgentCloud>[] => {
        const agentCloudOptions: IListBoxItem<TaskAgentCloud>[] = [];

        this.props.storesHub.agentCloudStore.state.forEach(agentCloud => {
            agentCloudOptions.push({
                id: agentCloud.id.toString(),
                text: agentCloud.name,
                data: agentCloud
            });
        });

        return agentCloudOptions;
    }

    private _okOnClick = (): void => {
        if (this.state.poolName) {
            if (this._project) {
                const newQueue: TaskAgentQueue = {
                    name: this.state.poolName
                } as TaskAgentQueue;

                let agentCloudId: number | undefined = undefined;
                if (this.props.showAgentCloudDropdown && this.state.selectedAgentCloud) {
                    agentCloudId = this.state.selectedAgentCloud.agentCloudId;
                }

                this.props.actionCreator.addAgentQueue(newQueue, this._project.id, this.state.authorizeAllPipelines, this.state.poolDescription, agentCloudId);
                this.props.actionCreator.dismissCreatePoolPanel();
            }
            else {
                const newPool: TaskAgentPool = {
                    name: this.state.poolName,
                    autoProvision: this.state.autoProvisionProjectPools
                } as TaskAgentPool;

                if (this.props.showAgentCloudDropdown && this.state.selectedAgentCloud) {
                    newPool.agentCloudId = this.state.selectedAgentCloud.agentCloudId;
                    newPool.isHosted = false;
                }

                if (this._showGrantAccessPermissionToAllPipelines && this.state.authorizeAllPipelines) {
                    newPool.properties = { "System.AutoAuthorize": true };
                }

                this.props.actionCreator.addAgentPool(newPool, this.state.poolDescription);
                this.props.actionCreator.dismissCreatePoolPanel();
            }
        }
    }

    private _shouldDisableSubmitButton = (poolName: string | undefined, selectedAgentCloud: TaskAgentCloud | undefined): boolean => {
        return !poolName || (this.props.showAgentCloudDropdown && !selectedAgentCloud);
    }

    private _getFooterButtonProps = (): IButtonProps[] => {
        return [
            {
                text: Resources.Create,
                primary: true,
                onClick: this._okOnClick,
                disabled: this.state.disableSubmitButton
            }
        ];
    }
}
